import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';

/* ─── Conic-gradient sphere — the system's only ornament ─── */
const ConicSphere = ({ size = 320, style = {} }) => (
  <div
    style={{
      width: size,
      height: size,
      borderRadius: '50%',
      background: `conic-gradient(
        rgb(87, 192, 241) 0%,
        rgb(74, 166, 232) 13%,
        rgb(134, 57, 162) 26%,
        rgb(239, 137, 159) 42%,
        rgb(234, 57, 42) 55%,
        rgb(239, 115, 53) 62%,
        rgb(245, 192, 68) 73%,
        rgb(245, 255, 84) 84%,
        rgb(160, 218, 83) 95%,
        rgb(87, 192, 241) 100%
      )`,
      flexShrink: 0,
      ...style,
    }}
  />
);

/* ─── Floating icon tile ─── */
const IconTile = ({ icon, label, style = {}, delay = '0s' }) => (
  <div
    className="animate-fade-up"
    style={{
      position: 'absolute',
      background: '#fff',
      border: '1px solid #000',
      borderRadius: 20,
      padding: '10px 16px',
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      fontSize: 13,
      fontWeight: 600,
      fontFamily: 'var(--font-mono)',
      boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
      animationDelay: delay,
      whiteSpace: 'nowrap',
      zIndex: 5,
      ...style,
    }}
  >
    <span style={{ fontSize: 18 }}>{icon}</span>
    {label}
  </div>
);

/* ─── Stat counter with animated number ─── */
const StatCounter = ({ value, label }) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let start = 0;
    const end = parseInt(value);
    const duration = 1200;
    const step = Math.ceil(end / (duration / 16));
    const timer = setInterval(() => {
      start = Math.min(start + step, end);
      setCount(start);
      if (start >= end) clearInterval(timer);
    }, 16);
    return () => clearInterval(timer);
  }, [value]);
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{
        fontFamily: 'var(--font-primary)',
        fontWeight: 800,
        fontSize: 56,
        lineHeight: 1,
        letterSpacing: '-0.04em',
        color: '#000',
      }}>
        {count}+
      </div>
      <div style={{
        fontFamily: 'var(--font-mono)',
        fontSize: 13,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        color: '#999',
        marginTop: 6,
      }}>
        {label}
      </div>
    </div>
  );
};

const Landing = () => {
  const navigate = useNavigate();
  const [heroVisible, setHeroVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setHeroVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  return (
    <div style={{ background: '#fff', minHeight: '100vh', overflowX: 'hidden' }}>

      {/* ── NAV ── */}
      <nav style={{
        position: 'fixed',
        top: 0, left: 0, right: 0,
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 48px',
        height: 64,
        background: 'rgba(255,255,255,0.92)',
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid #efefef',
      }}>
        <span style={{
          fontFamily: 'var(--font-primary)',
          fontWeight: 800,
          fontSize: 18,
          letterSpacing: '-0.02em',
          cursor: 'default',
        }}>
          Campus<span style={{ color: '#0000ff' }}>Reveal</span>
        </span>
        <button
          onClick={() => navigate('/home')}
          className="btn-primary"
          style={{ fontSize: 14, padding: '10px 22px' }}
        >
          Browse Colleges →
        </button>
      </nav>

      {/* ── HERO ── */}
      <section style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '120px 48px 80px',
        position: 'relative',
        overflow: 'hidden',
      }}>

        {/* Floating tiles */}
        <IconTile icon="🏛️" label="IIT Delhi" style={{ top: '18%', left: '8%' }} delay="0.3s" />
        <IconTile icon="⭐" label="4.8 Rating" style={{ top: '24%', right: '10%' }} delay="0.5s" />
        <IconTile icon="📝" label="Anonymous" style={{ bottom: '30%', left: '6%' }} delay="0.7s" />
        <IconTile icon="🔍" label="Verified Reviews" style={{ bottom: '32%', right: '8%' }} delay="0.9s" />

        {/* Sphere */}
        <div style={{
          position: 'absolute',
          bottom: '-80px',
          right: '-60px',
          opacity: 0.55,
          pointerEvents: 'none',
        }}>
          <ConicSphere size={380} />
        </div>

        {/* Headline stack */}
        <div style={{
          textAlign: 'center',
          position: 'relative',
          zIndex: 2,
          opacity: heroVisible ? 1 : 0,
          transition: 'opacity 0.5s ease',
        }}>

          {/* Tag line */}
          <div style={{
            display: 'inline-block',
            fontFamily: 'var(--font-mono)',
            fontSize: 12,
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: '0.12em',
            color: '#0000ff',
            background: 'rgba(0,0,255,0.07)',
            border: '1px solid rgba(0,0,255,0.2)',
            padding: '6px 14px',
            borderRadius: 9999,
            marginBottom: 32,
          }}>
            Anonymous · Unfiltered · Real
          </div>

          {/* Main headline */}
          <h1 className="animate-fade-up" style={{
            fontFamily: 'var(--font-primary)',
            fontWeight: 800,
            fontSize: 'clamp(52px, 8vw, 96px)',
            lineHeight: 0.96,
            letterSpacing: '-0.04em',
            color: '#000',
            marginBottom: 0,
          }}>
            CAMPUS<br />
            <span style={{ color: '#0000ff' }}>REVEAL</span>
          </h1>

          {/* Subtitle */}
          <p className="animate-fade-up delay-200" style={{
            fontFamily: 'var(--font-primary)',
            fontWeight: 400,
            fontSize: 22,
            lineHeight: 1.4,
            color: '#555',
            marginTop: 28,
            maxWidth: 480,
            marginLeft: 'auto',
            marginRight: 'auto',
          }}>
            Discover and review your dream colleges<br />
            <em style={{ fontStyle: 'normal', color: '#000', fontWeight: 600 }}>anonymously</em> — no filters, no fluff.
          </p>

          {/* CTAs */}
          <div className="animate-fade-up delay-300" style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 16,
            marginTop: 40,
          }}>
            <button
              onClick={() => navigate('/home')}
              className="btn-primary"
              id="landing-cta-primary"
              style={{ fontSize: 18, padding: '16px 36px' }}
            >
              Write a Review →
            </button>
            <button
              onClick={() => navigate('/home')}
              className="btn-ghost"
              id="landing-cta-browse"
              style={{ fontSize: 18, padding: '15px 36px' }}
            >
              Browse Colleges
            </button>
          </div>

          {/* Trust strip */}
          <div className="animate-fade-up delay-400" style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 24,
            marginTop: 36,
          }}>
            {['100% Anonymous', 'Student-Only Reviews', 'Free Forever'].map((text, i) => (
              <span key={i} style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 12,
                color: '#999',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}>
                <Check size={13} strokeWidth={3} style={{ color: '#00d37c', flexShrink: 0 }} />
                {text}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ── STATS BAND ── */}
      <section style={{
        background: '#121212',
        padding: '80px 48px',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Yellow accent panel */}
        <div style={{
          position: 'absolute',
          top: -40,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 2,
          height: 80,
          background: '#fffc52',
        }} />

        <div style={{
          maxWidth: 1000,
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 40,
          alignItems: 'center',
        }}>
          <div style={{
            background: '#fffc52',
            borderRadius: 90,
            padding: '48px 40px',
            textAlign: 'center',
          }}>
            <div style={{
              fontFamily: 'var(--font-primary)',
              fontWeight: 800,
              fontSize: 56,
              lineHeight: 1,
              letterSpacing: '-0.04em',
              color: '#000',
            }}>100+</div>
            <div style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 13,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: 'rgba(0,0,0,0.6)',
              marginTop: 8,
            }}>Colleges Listed</div>
          </div>

          <div style={{ textAlign: 'center' }}>
            <StatCounter value={500} label="Reviews Posted" />
            <div style={{
              width: 40, height: 1,
              background: '#333',
              margin: '20px auto 0',
            }} />
          </div>

          <div style={{ textAlign: 'center' }}>
            <div style={{
              fontFamily: 'var(--font-primary)',
              fontWeight: 800,
              fontSize: 56,
              lineHeight: 1,
              letterSpacing: '-0.04em',
              color: '#fff',
            }}>∞</div>
            <div style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 13,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: '#666',
              marginTop: 6,
            }}>Honest Opinions</div>
          </div>
        </div>

        {/* Tagline */}
        <div style={{
          maxWidth: 600,
          margin: '64px auto 0',
          textAlign: 'center',
        }}>
          <p style={{
            fontFamily: 'var(--font-primary)',
            fontSize: 22,
            fontWeight: 400,
            lineHeight: 1.5,
            color: '#bcc1c7',
          }}>
            Real reviews from real students.<br />
            <span style={{ color: '#fff', fontWeight: 600 }}>No college pays us. No review is faked.</span>
          </p>
          <button
            onClick={() => navigate('/home')}
            className="btn-primary"
            style={{ marginTop: 32 }}
          >
            Explore All Colleges →
          </button>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section style={{ padding: '100px 48px', background: '#fff' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <div style={{ marginBottom: 56, textAlign: 'center' }}>
            <span style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 12,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              color: '#999',
            }}>How it works</span>
            <h2 style={{
              fontFamily: 'var(--font-primary)',
              fontWeight: 800,
              fontSize: 42,
              letterSpacing: '-0.03em',
              marginTop: 12,
            }}>Three steps to the truth</h2>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 24,
          }}>
            {[
              { num: '01', title: 'Find Your College', body: 'Search from 100+ listed institutions across India.' },
              { num: '02', title: 'Read the Reviews', body: 'See honest, unfiltered opinions from current & past students.' },
              { num: '03', title: 'Share Your Experience', body: 'Add your anonymous review in under 2 minutes.' },
            ].map(({ num, title, body }, i) => (
              <div
                key={num}
                className="animate-fade-up"
                style={{
                  background: i === 1 ? '#0000ff' : '#f4f4f4',
                  borderRadius: 40,
                  padding: '40px 32px',
                  animationDelay: `${i * 0.15}s`,
                }}
              >
                <div style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 12,
                  fontWeight: 600,
                  letterSpacing: '0.1em',
                  color: i === 1 ? 'rgba(255,255,255,0.5)' : '#999',
                  marginBottom: 20,
                }}>{num}</div>
                <h3 style={{
                  fontFamily: 'var(--font-primary)',
                  fontWeight: 700,
                  fontSize: 22,
                  color: i === 1 ? '#fff' : '#000',
                  marginBottom: 12,
                }}>{title}</h3>
                <p style={{
                  fontFamily: 'var(--font-primary)',
                  fontSize: 15,
                  lineHeight: 1.6,
                  color: i === 1 ? 'rgba(255,255,255,0.7)' : '#666',
                }}>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{
        background: '#121212',
        padding: '40px 48px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 16,
      }}>
        <span style={{
          fontFamily: 'var(--font-primary)',
          fontWeight: 700,
          fontSize: 16,
          color: '#fff',
        }}>
          Campus<span style={{ color: '#0000ff' }}>Reveal</span>
        </span>
        <span style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 12,
          color: '#555',
        }}>
          Built for students, by students.
        </span>
      </footer>
    </div>
  );
};

export default Landing;
